import {
  computed,
  inject,
  Injectable,
  signal,
  Signal,
  WritableSignal,
} from "@angular/core";
import { BookingStatus, EntityStatus } from "../../enums";
import { PathwaysApiService } from "../api/treatment/pathways-api.service";
import { LoggedUserService } from "@keus-template/auth";
import { PathwayStatusService } from "./pathway-status.service";
import { PaymentModalManagerService } from "../ui/payment-modal-manager.service";
import {
  ResponseTestDto,
  ResponseVisitDto,
  CompletePathwayDto,
  CompletePhaseDto,
} from "../../api/models";

/**
 * Service responsible for managing pathway state.
 * Centralizes data access and state management for the pathway view.
 * 
 * This service provides reactive signals for pathway data with automatic
 * filtering by active phase to ensure only relevant appointments are shown.
 * 
 * @example
 * ```typescript
 * // In component
 * constructor(private pathwayState: PathwayStateService) {}
 * 
 * ngOnInit() {
 *   this.pathwayState.load();
 *   // Access filtered appointments
 *   const appointments = this.pathwayState.next4Appointments();
 * }
 * ```
 */
@Injectable({
  providedIn: "root",
})
export class PathwayStateService {
  /** Injected service for logged user data */
  private readonly _loggedUserService: LoggedUserService =
    inject(LoggedUserService);

  private readonly _pathwaysApiService: PathwaysApiService =
    inject(PathwaysApiService);

  /** Injected pathway status service */
  private readonly _pathwayStatusService: PathwayStatusService =
    inject(PathwayStatusService);

  /** Payment modal manager for clearing flags when pathway becomes active */
  private readonly _paymentModalManager: PaymentModalManagerService =
    inject(PaymentModalManagerService);

  /** Main state container for pathway data */
  private readonly _pathwayData: WritableSignal<CompletePathwayDto | null> =
    signal<CompletePathwayDto | null>(null);

  /**
   * Helper method to get all appointments from the active phase
   * @private
   */
  private _getActivePhaseAppointments(): (ResponseVisitDto | ResponseTestDto)[] {
    const activePhase = this.activePhase();
    if (!activePhase) return [];

    const appointments: (ResponseVisitDto | ResponseTestDto)[] = [];
    
    if (activePhase.visits?.length) {
      appointments.push(...activePhase.visits);
    }
    if (activePhase.tests?.length) {
      appointments.push(...activePhase.tests);
    }
    
    return appointments;
  }

  /**
   * Signal providing the next 2 upcoming reservations from the active phase only.
   * Filters for SCHEDULED status and future dates, sorted chronologically.
   */
  public readonly next2Reservations: Signal<
    (ResponseVisitDto | ResponseTestDto)[]
  > = computed(() => {
    const appointments = this._getActivePhaseAppointments();
    if (!appointments.length) return [];

    const now = Date.now();
    
    return appointments
      .filter((item) => {
        // Must have valid booking date and SCHEDULED status
        if (!item.bookingDate || item.status !== BookingStatus.SCHEDULED) {
          return false;
        }
        // Must be in the future
        return new Date(item.bookingDate).getTime() >= now;
      })
      .sort((a, b) => {
        // Sort by date ascending (earliest first)
        const dateA = new Date(a.bookingDate!).getTime();
        const dateB = new Date(b.bookingDate!).getTime();
        return dateA - dateB;
      })
      .slice(0, 2);
  });

  /**
   * Signal providing up to 4 appointments to be booked from the active phase only.
   * Includes items with PENDING or SCHEDULED status.
   */
  public readonly next4Appointments: Signal<
    (ResponseVisitDto | ResponseTestDto)[]
  > = computed(() => {
    const appointments = this._getActivePhaseAppointments();
    if (!appointments.length) return [];

    // Filter for PENDING or SCHEDULED status
    return appointments
      .filter(item => 
        item.status === BookingStatus.PENDING || 
        item.status === BookingStatus.SCHEDULED
      )
      .slice(0, 4);
  });

  /**
   * Signal providing all appointments from the active phase.
   * Includes all appointments regardless of status for comprehensive view.
   */
  public readonly allActivePhaseAppointments: Signal<
    (ResponseVisitDto | ResponseTestDto)[]
  > = computed(() => {
    return this._getActivePhaseAppointments();
  });

  /** Signal providing pathway title */
  public readonly title: Signal<string> = computed(
    () => this._pathwayData()?.title || "",
  );

  /** Signal providing pathway description */
  public readonly description: Signal<string> = computed(
    () => this._pathwayData()?.description || "",
  );

  /** Signal providing the current pathway type */
  public readonly pathwayType: Signal<string | undefined> = computed(
    () => this._pathwayData()?.pathwayType,
  );

  /** Signal providing human-readable name of current pathway */
  public readonly pathwayTypeLabel: Signal<string> = computed(() => {
    const pathwayType = this.pathwayType();
    if (!pathwayType) return "";

    // Map pathway type enum to readable label with gender
    if (pathwayType === "PMA") return "PMA";
    if (pathwayType === "SOCIAL_FREEZING_MALE") return "Social Freezing Maschile";
    if (pathwayType === "SOCIAL_FREEZING_FEMALE") return "Social Freezing Femminile";
    if (pathwayType === "BENESSERE_FERTILE_MALE") return "Benessere Fertile Maschile";
    if (pathwayType === "BENESSERE_FERTILE_FEMALE") return "Benessere Fertile Femminile";
    if (pathwayType === "FERTILITY_CHECKIN_MALE") return "Fertility Check-in Maschile";
    if (pathwayType === "FERTILITY_CHECKIN_FEMALE") return "Fertility Check-in Femminile";
    if (pathwayType === "OTHER") return "Altro";

    return "";
  });

  /** Signal providing the selected pathway type from questionnaire */
  public readonly selection: Signal<string | undefined> = computed(
    () => this._pathwayData()?.selection,
  );

  /** Signal providing human-readable name of selected pathway (the direction) */
  public readonly selectionLabel: Signal<string> = computed(() => {
    const selection = this.selection();
    if (!selection) return "";

    // Map pathway type enum to readable label with gender
    if (selection === "PMA") return "PMA";
    if (selection === "SOCIAL_FREEZING_MALE") return "Social Freezing Maschile";
    if (selection === "SOCIAL_FREEZING_FEMALE") return "Social Freezing Femminile";
    if (selection === "BENESSERE_FERTILE_MALE") return "Benessere Fertile Maschile";
    if (selection === "BENESSERE_FERTILE_FEMALE") return "Benessere Fertile Femminile";
    if (selection === "FERTILITY_CHECKIN_MALE") return "Fertility Check-in Maschile";
    if (selection === "FERTILITY_CHECKIN_FEMALE") return "Fertility Check-in Femminile";
    if (selection === "OTHER") return "Altro";

    return "";
  });

  /** Signal providing the currently active phase, or null if none */
  public readonly activePhase: Signal<CompletePhaseDto | null> = computed(() => {
    const pathwayData = this._pathwayData();
    if (!pathwayData?.phases) return null;
    // Find the active phase
    return pathwayData.phases.find(
      (phase: CompletePhaseDto) => phase.status === EntityStatus.ACTIVE,
    ) || null;
  });

  /** Signal providing current step/phase name */
  public readonly currentStepName: Signal<string> = computed(() => {
    return this.activePhase()?.name || "";
  });

  /** Signal providing current step/phase description */
  public readonly currentStepDescription: Signal<string> = computed(() => {
    const pathwayData = this._pathwayData();
    if (!pathwayData?.phases) return "";

    // Find the active phase
    const activePhase = pathwayData.phases.find(
      (phase: CompletePhaseDto) => phase.status === EntityStatus.ACTIVE,
    );
    return activePhase?.description || "";
  });

  /** Signal providing array of all steps/phases */
  public readonly steps: Signal<CompletePhaseDto[]> = computed(
    () => this._pathwayData()?.phases || [],
  );

  /** Signal providing full doctor object */
  public readonly doctorObject: Signal<any | null> = computed(() => {
    const pathwayData = this._pathwayData();
    if (!pathwayData) return null;

    // Fallback to constructing object from individual fields
    return {
      id: pathwayData.doctorId,
      firstName: pathwayData.doctorFirstName,
      lastName: pathwayData.doctorLastName,
      specialization: pathwayData.doctorSpecialization,
    };
  });

  /** Signal providing full clinic object */
  public readonly clinicObject: Signal<any | null> = computed(() => {
    const pathwayData = this._pathwayData();
    if (!pathwayData) return null;

    // Fallback to constructing object from individual fields
    return {
      id: pathwayData.clinicId,
      name: pathwayData.clinicName,
      city: pathwayData.clinicCity,
      email: pathwayData.clinicEmail,
    };
  });

  /** Signal providing full psychologist object */
  public readonly psychoObject: Signal<any | null> = computed(() => {
    const pathwayData = this._pathwayData();
    if (!pathwayData || !pathwayData.psychoId) return null;

    // Fallback to constructing object from individual fields
    return {
      id: pathwayData.psychoId,
      firstName: pathwayData.psychoFirstName,
      lastName: pathwayData.psychoLastName,
    };
  });

  /**
   * Loads the active pathway for the currently logged in patient
   * This method gets the patient ID from the logged user service
   * and fetches the active pathway data
   */
  public load(): void {
    const user = this._loggedUserService.currentUser();
    if (!user?.id) return;

    this._pathwaysApiService.findActiveByPatientId$(user.id).subscribe({
      next: (pathwayData) => {
        this._pathwayData.set(pathwayData);
        this._pathwayStatusService.updateFromPathway(pathwayData);
        
        // Clear payment modal flags when pathway becomes active (payment completed)
        if (pathwayData?.status === 'active' && pathwayData.createdAt) {
          this._paymentModalManager.clearModalFlags(pathwayData.createdAt);
        }
      },
      error: (error) => {
        console.error('Error loading pathway data:', error);
      }
    });
  }

  /**
   * Resets the entire pathway state
   * Returns to initial state with no data, loading, or errors
   */
  public resetState(): void {
    this._pathwayData.set(null);
    this._pathwayStatusService.reset();
  }
}
